<template>
  <div
    v-if="!isFullscreen"
    :data-role="fixed ? 'fixed-toolbar' : 'inline-toolbar'"
    :data-transparent-toolbar="transparent"
    :style="customStyles"
    :class="['v-toolbar__wrapper', { '--dark': isDarkBackground }]"
  >
    <div
      :class="[
        {
          '--transparent': transparent,
          '--flat': flat,
          '--inline': inline,
          '--fixed top-0 left-0': fixed && !inline
        },
        'v-toolbar'
      ]"
      id="toolbar"
    >
      <div class="v-toolbar__container" ref="toolbarContainer">
        <div class="v-toolbar__content">
          <div class="v-toolbar__left">
            <v-button
              v-if="back"
              class="v-toolbar__button"
              round
              ghost
              data-cy="back"
              data-role="back"
              @click="attemptBack"
            >
              <v-icon icon="arrow_back" small rtl-flip />
            </v-button>

            <v-button
              v-if="withMenu && !isDesktop"
              round
              ghost
              class="v-toolbar__button"
              data-cy="menu"
              @click="toggleSidebar"
            >
              <v-icon icon="menu" small />
            </v-button>

            <slot name="left" />

            <template v-if="showLeftLogo">
              <v-link class="d-flex align-items-center" to="/">
                <toolbar-logo
                  v-if="logo"
                  :logo="toolbar.logo"
                  :fallback-text="app.name"
                />
              </v-link>
            </template>
          </div>

          <div class="v-toolbar__center">
            <horizontal-toolbar-navigation
              v-if="
                withNavigation && isDesktop && !transparent && !isTabbarShown
              "
            />

            <template v-if="showCenteredLogo">
              <v-link class="d-flex align-items-center" to="/">
                <toolbar-logo
                  v-if="logo"
                  :logo="toolbar.logo"
                  :fallback-text="app.name"
                />
              </v-link>
            </template>

            <slot name="center" />
          </div>

          <div class="v-toolbar__right">
            <div id="v-toolbar-right-portal"></div>
            <slot name="right" />

            <v-button
              v-if="withMenu && isDesktop"
              round
              ghost
              @click="toggleSidebar"
              class="v-toolbar__button"
            >
              <v-icon icon="menu" />
            </v-button>
          </div>
        </div>

        <slot name="append" />
      </div>
    </div>

    <div v-if="showToolbarSpacer" class="v-toolbar__spacer">
      <div class="v-toolbar__spacer--padding">
        <div class="v-toolbar__spacer--border" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapGetters } from "vuex"
import EventBus, { EVENTS } from "@/lib/event-bus"
import { generateStyleVariables } from "@/lib/styles-utils"

import enumValidator from "@/lib/validators/enum-validator"
import { isDarkTextColor, rgbToHex } from "@shared/determine-text-color"
import { withRatio } from "@shared/block-ratio"

import ToolbarLogo from "@/components/toolbar/Logo"
import HorizontalToolbarNavigation from "@/components/horizontal-toolbar/Navigation"

export default {
  components: {
    ToolbarLogo,
    HorizontalToolbarNavigation
  },

  props: {
    transparent: { type: Boolean, default: false },
    logo: { type: Boolean, default: true },
    fixed: { type: Boolean, default: false },
    back: { type: Boolean, default: true },
    align: {
      type: String,
      default: null,
      validator: enumValidator(["left", "center"])
    },
    emitOnExitAttempt: { type: Boolean, default: false },
    flat: { type: Boolean, default: false },
    useAccountStyles: { type: Boolean, default: false },
    withMenu: { type: Boolean, default: false },
    pageId: { type: [Number, String], default: null },
    withNavigation: { type: Boolean, default: false },
    inline: { type: Boolean, default: false }
  },

  data() {
    return {
      isDarkBackground: false
    }
  },

  computed: {
    ...mapState(["global"]),
    ...mapState("videos", ["isFullscreen"]),
    ...mapState("responsive", ["isDesktop"]),
    ...mapGetters("tabbar", ["isTabbarShown"]),

    app() {
      return this.global?.app || {}
    },

    toolbar() {
      return this.global?.toolbar || {}
    },

    customStyles() {
      const styles = {
        "--mvt-logo-height": withRatio(50, this.toolbar.logo_height_ratio, {
          unit: "%"
        })
      }

      return this.useAccountStyles
        ? {
            "--mvt-background-color": this.toolbar.background_color,
            ...styles,
            ...generateStyleVariables(this.toolbar.styles)
          }
        : styles
    },

    hasSlotData() {
      return !!this.$slots.left || !!this.$slots.center
    },

    showLeftLogo() {
      return this.isDesktop && !this.transparent && !this.hasSlotData
    },

    showCenteredLogo() {
      return !this.isDesktop && !this.transparent && !this.hasSlotData
    },

    showToolbarSpacer() {
      return this.fixed && !this.transparent && !this.inline
    }
  },

  watch: {
    toolbar: {
      immediate: true,
      handler() {
        this.setupDarkTextColor()
      }
    },

    isDesktop() {
      this.setupDarkTextColor()
    },

    transparent() {
      this.setupDarkTextColor()
    }
  },

  methods: {
    ...mapMutations("tabbar", ["toggleSidebar"]),

    goBack() {
      this.$router.back()
    },

    attemptBack() {
      if (this.pageId && EventBus.hasListener(EVENTS.PAGE_EXIT_ATTEMPTED)) {
        EventBus.$emit(EVENTS.PAGE_EXIT_ATTEMPTED, {
          pageId: this.pageId
        })
      } else {
        this.goBack()
      }
    },

    setupDarkTextColor() {
      this.$nextTick(() => {
        if (!this.useAccountStyles) return false

        let backgroundColor = null

        if (this.$refs.toolbarContainer) {
          const style = getComputedStyle(this.$refs.toolbarContainer)
          backgroundColor = style.getPropertyValue("background-color")
        }

        backgroundColor = backgroundColor
          ? rgbToHex(backgroundColor)
          : this.toolbar?.background_color

        this.isDarkBackground = !isDarkTextColor(backgroundColor)
      })
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";
@import "@/assets/styles/mixins";
$content--max-width--spacer: 224px;

.v-toolbar__wrapper {
  --fallback-border-width: 0 0 1px 0;
  --fallback-border-style: solid;
  --fallback-border-color: var(--color-ui--grey-20);
  --fallback-background-color: rgba(var(--color-rgb--white), 0.86);
  --fallback-padding: 0 #{$size--2};
  --fallback-highlight-color: var(--primary-brand-color-hex);
  --fallback-text-color: #{$color-fixed--grey-90};
  --fallback-button-text-color: #{$color-ui--grey-70};
  --fallback-hover-color: rgba(0, 0, 0, 0.05);

  position: relative;

  &.--dark {
    --fallback-border-color: #{$color-ui--grey-80};
    --fallback-text-color: #{$color-ui--white};
    --fallback-button-text-color: #{$color-ui--white};
  }

  #toolbar.v-toolbar:not(.--transparent):not(.--inline) {
    .v-toolbar__container {
      @include responsive-style-var(margin, margin);
      @include responsive-style-var(
        border-width,
        border-width,
        var(--fallback-border-width)
      );
      @include responsive-style-var(
        border-style,
        border-style,
        var(--fallback-border-style)
      );
      @include responsive-style-var(
        border-color,
        border-color,
        var(--fallback-border-color)
      );
      @include responsive-style-var(border-radius, border-radius);
      @include responsive-style-var(box-shadow, shadow);
      @include responsive-style-var(
        background-color,
        background-color,
        var(--fallback-background-color)
      );

      @include style-var(max-width, navigation-max-width, 100%);

      backdrop-filter: blur(var(--mvt-blur));

      .v-toolbar__content {
        @include responsive-style-var(
          padding,
          padding,
          var(--fallback-padding)
        );
        max-width: var(
          --mvt-content-max-width,
          calc(#{$content--max-width} + #{$content--max-width--spacer})
        );

        .v-toolbar__button {
          @include style-var(
            color,
            text-color,
            var(--fallback-button-text-color)
          );

          &:hover {
            background-color: var(--fallback-hover-color);
          }
        }
      }
    }
  }

  .v-toolbar__spacer {
    @include responsive-style-var(padding, margin);

    .v-toolbar__spacer--padding {
      @include responsive-style-var(padding, padding, var(--fallback-padding));
    }

    .v-toolbar__spacer--border {
      @include responsive-style-var(
        padding,
        border-width,
        var(--fallback-border-width)
      );
    }
  }

  @include desktop-viewport-scope {
    .v-toolbar__content {
      display: grid;
      grid-template-columns: minmax(min-content, 1fr) auto minmax(
          min-content,
          1fr
        );

      .v-toolbar__left {
        max-width: none;
      }

      .v-toolbar__center {
        flex: none;
        max-width: none;
      }

      .v-toolbar__right {
        min-width: 0;
      }
    }

    .v-toolbar:not(.--transparent) {
      .v-toolbar__left {
        padding-right: $size--8;

        .v-toolbar__button:last-child {
          margin-right: $size--5;
        }
      }
    }
  }

  @include mobile-viewport-scope {
    .v-toolbar__content {
      .v-toolbar__center {
        flex: 1 2 46%;
      }

      .v-toolbar__left,
      .v-toolbar__right {
        flex: 0 1 27%;
      }
    }
  }
}
</style>

<style lang="scss">
@import "@/assets/styles/variables";
@import "@/assets/styles/mixins";

:root {
  --toolbar-height: 3.5rem;

  @media screen and (min-width: $breakpoint--xl) {
    --toolbar-height: 4.5rem;
  }
}

.v-toolbar {
  display: flex;
  justify-content: center;
  background-clip: padding-box;
  user-select: none;
  position: relative;
  z-index: $toolbar--z-index;
  color: var(--toolbar-color, $color-ui--grey-90);
  font-weight: $font-weight--base;
  width: 100%;

  .v-toolbar__container {
    box-sizing: content-box;
    width: 100%;
    min-height: var(--toolbar-height);
    padding-top: env(safe-area-inset-top);

    .v-toolbar__content {
      box-sizing: border-box;
      width: 100%;

      .marker-dot {
        left: unset;
        right: $size--1;
      }
    }
  }

  &.--fixed {
    position: fixed;
  }

  &.--flat {
    box-shadow: none;

    .v-toolbar__content {
      box-shadow: none !important;
    }
  }

  &.--inline {
    position: relative;
    box-shadow: none;
    background-color: transparent;
    margin: 0 auto;
    border: none;

    .v-toolbar__container {
      padding-top: 0;
    }

    .v-toolbar__content {
      max-width: var(--container-width);
    }
  }

  &.--transparent {
    background: transparent;
    box-shadow: none;
    border: none;

    .v-toolbar__content {
      max-width: var(--container-width);
    }

    .v-toolbar__button {
      @include clickable(0.9);

      background: $color-ui--white;
      position: relative;
      top: $size--1;
      box-shadow: $box-shadow--medium;
      color: $color-ui--grey-70;
      border-radius: 100px;
      width: $size--9;
      height: $size--9;
      padding: 0 $size--4;

      &:last-child {
        margin-right: 0;
      }

      .v-loader {
        --mvt-loader-size: #{$size--6};
      }
    }

    .v-toolbar__left {
      .v-toolbar__button {
        margin-right: $size--2;
      }

      .v-toolbar__button:first-child {
        margin-left: 0;
      }
    }

    .v-toolbar__right {
      .v-toolbar__button {
        background: rgba($color-rgb--black, 0.2);
        color: $color-ui--white;
        box-shadow: none;
        margin-left: $size--2;

        &.--active {
          background: $color-ui--white;
          box-shadow: $box-shadow--medium;
          color: $color-ui--grey-70;
        }
      }
    }

    .v-toolbar__content {
      padding: 0 $size--2;
      max-width: $content--max-width;
    }
  }
}

@media screen and (min-width: $breakpoint--xl) {
  .v-toolbar:not(.--inline) {
    top: 0;
    z-index: $toolbar--z-index + 1;
  }
}

.--dark-mode {
  .v-toolbar {
    &.--transparent {
      .v-toolbar__button {
        border: 2px solid white;
      }

      .v-toolbar__right {
        .v-toolbar__button {
          border: none;
          color: white;
        }
      }
    }
  }
}

.--with-left-sidebar {
  [data-role="fixed-toolbar"] .v-toolbar {
    margin-left: $tabbar--desktop-width;
    width: calc(100% - #{$tabbar--desktop-width});
  }
}

.v-toolbar__container {
  .v-toolbar__content {
    display: flex;
    justify-content: space-between;
    flex-wrap: nowrap;
    white-space: nowrap;
    margin: 0 auto;
    padding: 0 $size--1;

    & > div {
      min-height: $toolbar--height;
      display: flex;
      align-items: center;
    }

    .v-toolbar__left {
      justify-content: flex-start;
      max-width: 80%;

      > a {
        height: 100%;
      }
    }

    .v-toolbar__center {
      align-items: center;
      justify-content: center;
      max-width: 80%;
      min-width: 0;

      > a {
        min-width: 0;
        overflow: hidden;
        height: 100%;
      }
    }

    .v-toolbar__right {
      display: flex;
      justify-content: flex-end;

      --mvt-loader-size: 1.25rem;
    }

    .v-toolbar__button {
      font-size: 1.35rem;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 $size--5;
      height: 100%;
      margin: 0;
      border: none;

      .v-icon {
        transform: scale(1);
      }
    }

    .v-toolbar__text {
      @include overflow-ellipsis;

      font-weight: $font-weight--bold;
      font-size: 1.35rem;
    }
  }
}

.v-toolbar__spacer,
.v-toolbar__spacer--padding,
.v-toolbar__spacer--border {
  box-sizing: content-box;
  min-height: calc(var(--toolbar-height) + env(safe-area-inset-top));
}

.v-toolbar__button {
  @include clickable(0.9);
}

[dir="rtl"] {
  .v-toolbar {
    &:not(.--transparent) {
      .v-toolbar-logo,
      .v-toolbar__text {
        margin-right: $size--5;
        margin-left: 0;
      }
    }
  }
}
</style>
